import { useState, useCallback, useRef, useEffect } from 'react';
import * as Yup from 'yup';

export const useNoteForm = ({ note, onSuccess, draft, onDraftChange }) => {
  const [formKeyIndex, setFormKeyIndex] = useState(0);
  const debounceTimerRef = useRef(null);

  const formSchema = Yup.object().shape({
    title: Yup.string().nullable(),
    body: Yup.string().required('Note is a required field'),
  });
  const isCreateNote = !note || !note.id;
  const externalOptions = [
    {
      label: 'Only visible to your team',
      value: false,
    },
    {
      label: 'Visible to your team and on shared lists',
      value: true,
    },
  ];

  // Use draft values for new notes if available
  const initialValues = isCreateNote
    ? {
        title: draft?.title || '',
        external: externalOptions[0],
        body: draft?.body || '',
      }
    : { ...note, external: externalOptions.find((opt) => opt.value === note.external) };

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  // Debounced handler for saving draft (only for new notes)
  const handleValuesChange = useCallback(
    (values) => {
      if (!onDraftChange || !isCreateNote) return;

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the save to avoid too many writes
      debounceTimerRef.current = setTimeout(() => {
        onDraftChange({
          title: values.title,
          body: values.body,
        });
      }, 500);
    },
    [onDraftChange, isCreateNote],
  );

  const onSubmit = (values) => {
    onSuccess(values, {
      resetForm: () => {
        setFormKeyIndex(formKeyIndex + 1);
      },
    });
  };

  return {
    formKeyIndex,
    formSchema,
    initialValues,
    externalOptions,
    isCreateNote,
    onSubmit,
    handleValuesChange,
  };
};
